import { TrendingUp, Target, Users, Calendar, UserPlus, BellPlus, LogIn, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

interface MobileHomeSummaryProps {
  greeting: string;
  userName: string;
  activeLeads: number;
  loginCount: number;
  loginAmount: number;
  loginTargetCount: number;
  loginTargetAmount: number;
  disbursementCount: number;
  disbursementAmount: number;
  disbursementTargetCount: number;
  disbursementTargetAmount: number;
  leadsAddedToday: number;
  vendorVisitsToday: number;
  incentiveEarned: number;
  onAddLead: () => void;
  onAddReminder: () => void;
  onIncentiveClick?: () => void;
  onAttendanceClick?: () => void;
  attendanceLabel?: 'attendance' | 'present' | 'absent' | 'holiday';
  // RM-specific
  isRelationalManager?: boolean;
  dsaPartnersThisMonth?: number;
  dsaPartnersTotal?: number;
  // Basic User
  isBasicUser?: boolean;
}

function formatCurrency(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

export function MobileHomeSummary({
  greeting,
  userName,
  activeLeads,
  loginCount,
  loginAmount,
  loginTargetCount,
  loginTargetAmount,
  disbursementCount,
  disbursementAmount,
  disbursementTargetCount,
  disbursementTargetAmount,
  leadsAddedToday,
  vendorVisitsToday,
  incentiveEarned,
  onAddLead,
  onAddReminder,
  onIncentiveClick,
  onAttendanceClick,
  attendanceLabel = 'attendance',
  isRelationalManager = false,
  dsaPartnersThisMonth = 0,
  dsaPartnersTotal = 0,
  isBasicUser = false,
}: MobileHomeSummaryProps) {
  const navigate = useNavigate();

  // ── BASIC USER VIEW ──────────────────────────────────────────────────────
  if (isBasicUser) {
    return (
      <Card className="md:hidden mb-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-900 border-green-200 shadow-sm">
        <CardContent className="p-3">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h2 className="text-base font-bold text-foreground">
                {greeting}, {userName}! 👋
              </h2>
              <p className="text-xs text-green-600 font-medium">
                {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs border-green-500 text-green-600 hover:bg-green-50 shadow-sm"
              onClick={onAttendanceClick}
            >
              {attendanceLabel === 'present' ? '✓ Present' : attendanceLabel === 'absent' ? 'Absent' : attendanceLabel === 'holiday' ? '🌴 Holiday' : 'Attendance'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── RELATIONAL MANAGER VIEW ──────────────────────────────────────────────
  if (isRelationalManager) {
    return (
      <Card className="md:hidden mb-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-900 border-green-200 shadow-sm">
        <div className="p-4">
          {/* Greeting */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-green-800 dark:text-green-100 truncate">
                  {greeting}, {userName}! 👋
                </h2>
                <p className="text-xs text-green-600 dark:text-green-300 mt-0.5">
                  {new Date().toLocaleDateString('en-IN', { weekday: 'long', month: 'short', day: 'numeric' })}
                </p>
              </div>
              {onAttendanceClick && (
                <Button variant="outline" size="sm" onClick={onAttendanceClick}
                  className={cn(
                    "flex-shrink-0 h-8 w-auto px-3 transition-colors",
                    attendanceLabel === 'present' && "border-green-500 bg-green-50 text-green-700",
                    attendanceLabel === 'absent' && "border-red-400 bg-red-50 text-red-600",
                    attendanceLabel === 'holiday' && "border-green-400 bg-green-50 text-green-700",
                    attendanceLabel === 'attendance' && "border-green-400 text-green-700",
                  )}>
                  <span className="text-xs font-medium">
                    {attendanceLabel === 'present' ? '✓ Present' : attendanceLabel === 'absent' ? '✗ Absent' : attendanceLabel === 'holiday' ? '🌴 Holiday' : 'Attendance'}
                  </span>
                </Button>
              )}
            </div>
          </motion.div>

          {/* RM Stats: Added This Month + Add Reminder side by side */}
          <div className="grid grid-cols-2 gap-2 mb-1">
            {/* Added DSA Partners This Month */}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
              <Card variant="elevated" className="h-full">
                <CardContent className="p-2.5">
                  <div className="flex flex-col items-center justify-center text-center gap-0.5">
                    <div className="flex items-center gap-1">
                      <div className="flex h-5 w-5 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/30">
                        <Plus className="h-3 w-3 text-orange-600" />
                      </div>
                      <span className="text-[9px] text-muted-foreground uppercase font-medium">DSA Partners</span>
                    </div>
                    <p className="text-lg font-bold text-orange-600 tracking-tight">{dsaPartnersThisMonth}</p>
                    <p className="text-[9px] text-muted-foreground">Added This Month</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Add Reminder as a box */}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 }}>
              <Card variant="elevated" className="h-full cursor-pointer hover:shadow-md transition-shadow border-green-200"
                onClick={onAddReminder}>
                <CardContent className="p-2.5 h-full flex flex-col items-center justify-center gap-1">
                  <div className="flex h-5 w-5 items-center justify-center rounded-lg bg-green-100">
                    <BellPlus className="h-3 w-3 text-green-600" />
                  </div>
                  <p className="text-xs font-semibold text-green-600 text-center">Add Reminder</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="md:hidden mb-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-900 border-green-200 shadow-sm">
      <div className="p-4">
        {/* Greeting with Logout Button */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-3"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-green-800 dark:text-green-100 truncate">
                {greeting}, {userName}! 👋
              </h2>
              <p className="text-xs text-green-600 dark:text-green-300 mt-0.5">
                {new Date().toLocaleDateString('en-IN', { 
                  weekday: 'long', 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            
            {onAttendanceClick && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAttendanceClick}
                className={cn(
                  "flex-shrink-0 h-8 w-auto px-3 transition-colors",
                  attendanceLabel === 'present' && "border-green-500 bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-800 hover:border-green-600",
                  attendanceLabel === 'absent' && "border-red-400 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 hover:border-red-500",
                  attendanceLabel === 'holiday' && "border-green-400 bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-800 hover:border-green-500",
                  attendanceLabel === 'attendance' && "border-green-400 text-green-700 hover:bg-green-100 hover:text-green-800 hover:border-green-500"
                )}
                title="Attendance"
              >
                <span className="text-xs font-medium">
                  {attendanceLabel === 'present' ? '✓ Present' : attendanceLabel === 'absent' ? '✗ Absent' : attendanceLabel === 'holiday' ? '🌴 Holiday' : 'Attendance'}
                </span>
              </Button>
            )}
          </div>
        </motion.div>

        {/* Stats Grid - 3x2 */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {/* Row 1 - Clickable cards that navigate to Leads */}
          
          {/* Active Leads */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="cursor-pointer"
            onClick={() => navigate('/leads', { state: { filter: 'all' } })}
          >
            <Card variant="elevated" className="h-full hover:shadow-md transition-shadow">
              <CardContent className="p-2.5">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1">
                    <div className="flex h-5 w-5 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                      <Users className="h-3 w-3 text-blue-600" />
                    </div>
                    <span className="text-[9px] text-muted-foreground uppercase font-medium">Leads</span>
                  </div>
                  <p className="text-lg font-bold text-blue-600 tracking-tight">
                    {activeLeads}
                  </p>
                  <p className="text-[9px] text-muted-foreground">In pipeline</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Login Stats */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15 }}
            className="cursor-pointer"
            onClick={() => navigate('/leads', { state: { filter: 'logged_in' } })}
          >
            <Card variant="accent" className="h-full hover:shadow-md transition-shadow">
              <CardContent className="p-2.5">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1">
                    <div className="flex h-5 w-5 items-center justify-center rounded-lg bg-accent/20">
                      <LogIn className="h-3 w-3 text-accent" />
                    </div>
                    <span className="text-[9px] text-muted-foreground uppercase font-medium">Login</span>
                  </div>
                  <p className="text-lg font-bold text-accent tracking-tight">
                    {`${loginCount}/${loginTargetCount}`}
                  </p>
                  <p className="text-[9px] text-muted-foreground truncate">
                    {`${formatCurrency(loginAmount)}/${formatCurrency(loginTargetAmount)}`}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Disbursement Stats */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="cursor-pointer"
            onClick={() => navigate('/leads', { state: { filter: 'disbursed' } })}
          >
            <Card variant="gold" className="h-full hover:shadow-md transition-shadow">
              <CardContent className="p-2.5">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1">
                    <div className="flex h-5 w-5 items-center justify-center rounded-lg bg-gold/20">
                      <TrendingUp className="h-3 w-3 text-gold" />
                    </div>
                    <span className="text-[9px] text-muted-foreground uppercase font-medium">Disbursed</span>
                  </div>
                  <p className="text-lg font-bold text-gold tracking-tight">
                    {`${disbursementCount}/${disbursementTargetCount}`}
                  </p>
                  <p className="text-[9px] text-muted-foreground truncate">
                    {`${formatCurrency(disbursementAmount)}/${formatCurrency(disbursementTargetAmount)}`}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Row 2 - Info-only cards (NOT clickable, no navigation) */}
          
          {/* Leads Added Today */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25 }}
          >
            <Card variant="elevated" className="h-full">
              <CardContent className="p-2.5">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1">
                    <div className="flex h-5 w-5 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
                      <Plus className="h-3 w-3 text-green-600" />
                    </div>
                    <span className="text-[9px] text-muted-foreground uppercase font-medium">Leads</span>
                  </div>
                  <p className="text-lg font-bold text-green-600 tracking-tight">
                    {leadsAddedToday}
                  </p>
                  <p className="text-[9px] text-muted-foreground">Added Today</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Vendor Visits Today */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Card variant="elevated" className="h-full">
              <CardContent className="p-2.5">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1">
                    <div className="flex h-5 w-5 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/30">
                      <Calendar className="h-3 w-3 text-orange-600" />
                    </div>
                    <span className="text-[9px] text-muted-foreground uppercase font-medium">Vendors</span>
                  </div>
                  <p className="text-lg font-bold text-orange-600 tracking-tight">
                    {vendorVisitsToday}
                  </p>
                  <p className="text-[9px] text-muted-foreground">Visits Today</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Incentive Earned */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35 }}
            className="cursor-pointer"
            onClick={onIncentiveClick}
          >
            <Card variant="elevated" className="h-full hover:shadow-md transition-shadow bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-950 dark:to-amber-950 border-yellow-300">
              <CardContent className="p-2.5">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1">
                    <div className="flex h-5 w-5 items-center justify-center rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                      <TrendingUp className="h-3 w-3 text-yellow-600" />
                    </div>
                    <span className="text-[9px] text-muted-foreground uppercase font-medium">Incentive</span>
                  </div>
                  <p className="text-base font-bold text-yellow-600 tracking-tight">
                    {formatCurrency(incentiveEarned)}
                  </p>
                  <p className="text-[9px] text-muted-foreground">
                    {new Date().toLocaleDateString('en-IN', { month: 'short' })}, {new Date().getFullYear()}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Quick Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex gap-2"
        >
          <Button 
            size="sm" 
            className="flex-1 bg-green-500 hover:bg-green-600 h-9 text-xs shadow-sm"
            onClick={onAddLead}
          >
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
            New Lead
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            className="flex-1 h-9 text-xs border-green-500 text-green-600 hover:bg-green-50 shadow-sm"
            onClick={onAddReminder}
          >
            <BellPlus className="h-3.5 w-3.5 mr-1.5" />
            Add Reminder
          </Button>
        </motion.div>
      </div>
    </Card>
  );
}